import { useCallback, useRef } from 'react';

import type { SlideOutProps } from '../types';

/**
 * Guards user-initiated dismissals (header close, `SlideOut.Close`, overlay
 * click, Escape). Imperative `close()` does not call this.
 *
 * A second dismissal while the guard is still running is rejected, so two
 * clicks cannot start two closes. A throw or rejection keeps the panel open.
 */
export function useCanClose(onBeforeClose: SlideOutProps['onBeforeClose']) {
  const onBeforeCloseRef = useRef(onBeforeClose);
  const isPendingRef = useRef(false);

  onBeforeCloseRef.current = onBeforeClose;

  return useCallback(async (): Promise<boolean> => {
    const beforeClose = onBeforeCloseRef.current;

    if (!beforeClose) {
      return true;
    }

    if (isPendingRef.current) {
      return false;
    }

    isPendingRef.current = true;

    try {
      return await beforeClose();
    } catch (error) {
      console.error('SlideOut onBeforeClose failed:', error);

      return false;
    } finally {
      isPendingRef.current = false;
    }
  }, []);
}
